/**
 * Product Catalog - Client-Side Filtering & Pagination
 * Handles lazy loading of products, filtering, and pagination
 */

// ============================================================================
// Configuration
// ============================================================================

const CACHE_CONFIG = {
	enabled: false, // SET TO true FOR PRODUCTION
	duration: 30 * 60 * 1000, // 30 minutes
	storageKey: 'product_catalog_cache',
};

const LOAD_CONFIG = {
	parallelLoads: 3, // Number of pages to load simultaneously
	searchDebounceMs: 300, // Debounce delay for name search (milliseconds)
	maxRetries: 3, // Maximum retry attempts for failed page loads
};

// ============================================================================
// State Management
// ============================================================================

class CatalogState {
	constructor() {
		this.allProducts = []; // Array of {id, title, titleLower, metafields, element}
		this.filteredProducts = []; // Currently filtered products
		this.currentPage = 1;
		this.itemsPerPage = 24; // Initial value for items per page
		this.totalPages = 1;
		this.totalProducts = 0;
		// Source pagination (how the Liquid sections paginate the collection).
		// Kept separate from totalPages, which updatePagination() overwrites with
		// the *display* page count (filteredProducts / itemsPerPage).
		this.sourcePageSize = 50;
		this.sourceTotalPages = 1;
		this.initialSourcePage = 1; // Source page rendered server-side into the DOM
		this.loadedPages = new Set(); // Track which pages are loaded
		this.pagesInFlight = new Set(); // Pages being fetched right now (not yet in loadedPages)
		this.productIds = new Set(); // Track loaded product IDs for fast duplicate detection
		this.isLoading = false;
		this.activeLoads = 0; // Track number of active parallel loads
		this.loadingQueue = []; // Priority queue for loading specific pages
		this.allDataLoaded = false;
		this.searchDebounceTimer = null; // Timer for debouncing search input
		this.pageRetries = new Map(); // Track retry count per page to prevent infinite loops
		this.visibleProductIds = new Set(); // Track currently visible products for optimization

		// Filter state
		this.filters = {
			production_type: '',
			filter_types: new Set(), // Changed to Set for multi-select
			moq: null,
			price_min: null,
			price_max: null,
			name: '',
		};

		// Collection info
		this.collectionHandle = '';

		// Cached DOM elements (populated on init)
		this.dom = {};
	}
}

const state = new CatalogState();

// ============================================================================
// Initialization
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
	initializeCatalog();
});

function initializeCatalog() {
	// Get collection info from section
	const section = document.getElementById('product-catalog');
	if (!section) return;

	state.collectionHandle = section.getAttribute('data-collection-handle');

	// Cache DOM elements for performance
	state.dom = {
		productGrid: document.getElementById('product-grid'),
		resultText: document.getElementById('result-text'),
		pageNumbers: document.getElementById('page-numbers'),
		btnPrevious: document.getElementById('btn-previous'),
		btnNext: document.getElementById('btn-next'),
		filterProductionWrapper: document.getElementById(
			'filter-production-wrapper',
		),
		filterProductionTrigger: document.getElementById(
			'filter-production-trigger',
		),
		filterProductionDropdown: document.getElementById(
			'filter-production-dropdown',
		),
		filterMoqWrapper: document.getElementById('filter-moq-wrapper'),
		filterMoqTrigger: document.getElementById('filter-moq-trigger'),
		filterMoqDropdown: document.getElementById('filter-moq-dropdown'),
		filterPriceWrapper: document.getElementById('filter-price-wrapper'),
		filterPriceTrigger: document.getElementById('filter-price-trigger'),
		filterPriceDropdown: document.getElementById('filter-price-dropdown'),
		filterName: document.getElementById('filter-name'),
		clearFilters: document.getElementById('clear-filters'),
		itemsPerPageWrapper: document.getElementById('items-per-page-wrapper'),
		itemsPerPageTrigger: document.getElementById('items-per-page-trigger'),
		itemsPerPageDropdown: document.getElementById('items-per-page-dropdown'),
		// Mobile filter elements
		showFiltersBtn: document.getElementById('show-filters-btn'),
		closeFiltersBtn: document.getElementById('close-filters-btn'),
		mobileFilterDrawer: document.getElementById('mobile-filter-drawer'),
		mobileFilterOverlay: document.getElementById('mobile-filter-overlay'),
		mobileFilterName: document.getElementById('mobile-filter-name'),
		mobileApplyBtn: document.getElementById('mobile-apply-btn'),
		mobileResetBtn: document.getElementById('mobile-reset-btn'),
	};

	// Parse initial pagination data
	const paginationData = JSON.parse(
		document.getElementById('pagination-data').textContent,
	);
	// totalPages is the *display* page count and is computed by updatePagination()
	state.totalProducts = paginationData.total_products;
	// current_page is a *source* page; the grid cursor is a display page, so
	// always start the grid at 1 and remember which source page the DOM holds
	state.initialSourcePage = paginationData.current_page || 1;
	state.currentPage = 1;
	state.sourceTotalPages = paginationData.total_pages;
	state.sourcePageSize = paginationData.per_page || 50;

	// Load initial products from DOM
	parseInitialProducts();

	// Set up event listeners
	setupEventListeners();

	// Parse filters from URL params
	parseFiltersFromURL();

	// Try to load from cache first
	console.log('🔍 Checking cache... (enabled:', CACHE_CONFIG.enabled, ')');
	const cacheLoaded = CACHE_CONFIG.enabled && loadFromCache();

	if (cacheLoaded) {
		console.log('✅ Using cached data, skipping background load');
	} else {
		console.log('📥 No valid cache, starting background load');
		// Start background loading immediately if filters are active
		// This ensures filtered results are populated as quickly as possible
		const hasActiveFilters =
			state.filters.production_type ||
			state.filters.filter_types.size > 0 ||
			state.filters.moq !== null ||
			state.filters.price_min !== null ||
			state.filters.name;

		if (hasActiveFilters) {
			// Start loading immediately when filters are active
			startBackgroundLoading();
		} else {
			// Delay background loading by 500ms for non-filtered views to prioritize initial render
			setTimeout(() => {
				startBackgroundLoading();
			}, 500);
		}
	}
	// Initial UI update after cache check and background load start
	applyFilters();
}

function parseInitialProducts() {
	const wrappers = document.querySelectorAll('.product-card-wrapper');

	wrappers.forEach((wrapper) => {
		const metadataScript = wrapper.querySelector('.product-metadata');
		if (!metadataScript) return;

		try {
			const productData = JSON.parse(metadataScript.textContent);

			state.allProducts.push({
				id: productData.id,
				title: productData.title,
				titleLower: productData.title.toLowerCase(), // Pre-compute lowercase for search
				url: productData.url,
				featured_image: productData.featured_image,
				featured_image_alt: productData.featured_image_alt,
				secondary_image: productData.secondary_image,
				metafields: productData.metafields,
				element: wrapper,
				rendered: false,
			});

			// Add to Set for fast duplicate detection
			state.productIds.add(productData.id);
		} catch (e) {
			console.error('Error parsing initial product metadata:', e, wrapper);
		}
	});

	// Mark the server-rendered page as loaded (not necessarily page 1)
	state.loadedPages.add(state.initialSourcePage);
}

// ============================================================================
// Background Loading
// ============================================================================

function startBackgroundLoading() {
	if (state.sourceTotalPages <= 1) {
		state.allDataLoaded = true;
		// Update UI to reflect that all data is loaded
		updateUI();
		displayProducts();
		return;
	}

	// Queue every source page except the one already rendered into the DOM
	for (let page = 1; page <= state.sourceTotalPages; page++) {
		if (page !== state.initialSourcePage) {
			state.loadingQueue.push(page);
		}
	}

	// Start loading multiple pages in parallel
	for (let i = 0; i < LOAD_CONFIG.parallelLoads; i++) {
		loadNextPage();
	}
}

async function loadNextPage() {
	if (state.loadingQueue.length === 0) {
		// Check if all loads are complete (no active loads and queue is empty)
		if (state.activeLoads === 0) {
			state.allDataLoaded = true;
			state.isLoading = false;
			updateUI();

			// Force a display update to refresh the result count
			displayProducts();

			// Save to cache
			if (CACHE_CONFIG.enabled) {
				console.log('💾 Attempting to save to cache...');
				saveToCache();
			}
		}
		return;
	}

	const pageToLoad = state.loadingQueue.shift();
	if (!pageToLoad) return;

	state.isLoading = true;
	state.activeLoads++; // Increment active load counter
	state.pagesInFlight.add(pageToLoad);

	try {
		// Use Section Rendering API for efficient JSON response
		// This avoids loading full HTML page with headers/footers
		// If a collection is selected, fetch from the collection URL to scope products correctly
		const basePath =
			state.collectionHandle && state.collectionHandle !== 'all'
				? `/collections/${state.collectionHandle}`
				: '';
		const response = await fetch(
			`${basePath}/?sections=chapter-v2-product-catalog-data&page=${pageToLoad}`,
		);

		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		const data = await response.json();
		const sectionHtml = data['chapter-v2-product-catalog-data'];

		if (!sectionHtml) {
			// Throw so the retry path below gets a chance, instead of silently
			// dropping every product on this page
			throw new Error('Section data not found in response');
		}

		// Parse the HTML response
		const parser = new DOMParser();
		const doc = parser.parseFromString(sectionHtml, 'text/html');

		// Extract product wrappers - look inside the products-data-page container
		const dataPage = doc.querySelector('.products-data-page');
		if (!dataPage) {
			throw new Error('No products-data-page found in response');
		}

		// Re-sync the source page count from the AJAX response. Both sections must
		// paginate by the same amount, but resync anyway so a mismatch degrades
		// gracefully instead of silently dropping products.
		const totalPagesFromAjax = parseInt(
			dataPage.getAttribute('data-total-pages'),
		);
		const totalProductsFromAjax = parseInt(
			dataPage.getAttribute('data-total-products'),
		);
		const pageSizeFromAjax = parseInt(dataPage.getAttribute('data-page-size'));

		// The data section is the authority on how products are chunked, since it
		// serves every page after the first
		if (pageSizeFromAjax) {
			state.sourcePageSize = pageSizeFromAjax;
		}

		if (totalPagesFromAjax && totalPagesFromAjax !== state.sourceTotalPages) {
			state.sourceTotalPages = totalPagesFromAjax;
			if (totalProductsFromAjax) {
				state.totalProducts = totalProductsFromAjax;
			}

			// Drop queued pages that no longer exist
			state.loadingQueue = state.loadingQueue.filter(
				(page) => page <= state.sourceTotalPages,
			);

			// Queue any pages we haven't loaded and aren't already fetching
			for (let page = 1; page <= state.sourceTotalPages; page++) {
				if (
					!state.loadedPages.has(page) &&
					!state.pagesInFlight.has(page) &&
					!state.loadingQueue.includes(page)
				) {
					state.loadingQueue.push(page);
				}
			}
		}

		const wrappers = dataPage.querySelectorAll('.product-card-wrapper');
		let addedCount = 0;
		wrappers.forEach((wrapper) => {
			const metadataScript = wrapper.querySelector('.product-metadata');
			if (!metadataScript) {
				console.warn('Product wrapper missing metadata script');
				return;
			}

			try {
				const productData = JSON.parse(metadataScript.textContent);

				// Check if product already exists using Set for O(1) lookup
				if (state.productIds.has(productData.id)) {
					return;
				}

				// Clone the wrapper to avoid reference issues
				const clonedWrapper = wrapper.cloneNode(true);

				// Add wrapper to grid (hidden by default)
				state.dom.productGrid.appendChild(clonedWrapper);

				// Store product data with reference to the cloned element
				state.allProducts.push({
					id: productData.id,
					title: productData.title,
					titleLower: productData.title.toLowerCase(), // Pre-compute lowercase for search
					url: productData.url,
					featured_image: productData.featured_image,
					featured_image_alt: productData.featured_image_alt,
					secondary_image: productData.secondary_image,
					metafields: productData.metafields,
					element: clonedWrapper,
					rendered: false, // Track if product card HTML has been rendered
				});

				// Add to Set for fast duplicate detection
				state.productIds.add(productData.id);

				addedCount++;
			} catch (e) {
				console.error('Error parsing product metadata:', e);
			}
		});

		state.loadedPages.add(pageToLoad);

		// Check if filters are active to determine if re-filtering is needed
		const hasActiveFilters =
			state.filters.production_type ||
			state.filters.filter_types.size > 0 ||
			state.filters.moq !== null ||
			state.filters.price_min !== null ||
			state.filters.name;

		if (hasActiveFilters) {
			// Save current page before re-filtering
			const savedPage = state.currentPage;

			// Re-apply filters to include newly loaded products
			const previousFilteredCount = state.filteredProducts.length;
			applyFiltersWithoutPageReset();

			// Restore page if we had results before, otherwise stay on page 1
			if (previousFilteredCount > 0) {
				state.currentPage = savedPage;
			}

			// Update display if new matching products were found
			displayProducts();
			updatePagination();
		} else {
			// Just update the filtered products list without resetting pagination
			state.filteredProducts = state.allProducts;
			updatePagination();

			// If the current page isn't fully populated yet (items per page can
			// exceed the source page size, e.g. showing 60 with 50 per fetch), fill
			// it in now instead of waiting for every page to finish loading.
			const startIndex = (state.currentPage - 1) * state.itemsPerPage;
			const expectedVisible = Math.max(
				0,
				Math.min(state.itemsPerPage, state.filteredProducts.length - startIndex),
			);

			if (state.visibleProductIds.size < expectedVisible) {
				// displayProducts() refreshes the result count itself
				displayProducts();
			} else {
				updateResultCount();
			}
		}
	} catch (error) {
		// Track retry count and only re-queue if under max retries
		const retryCount = state.pageRetries.get(pageToLoad) || 0;
		if (retryCount < LOAD_CONFIG.maxRetries) {
			state.pageRetries.set(pageToLoad, retryCount + 1);
			state.loadingQueue.push(pageToLoad);
			console.warn(
				`Retrying page ${pageToLoad} (attempt ${retryCount + 1}/${LOAD_CONFIG.maxRetries})`,
			);
		} else {
			console.error(
				`Failed to load page ${pageToLoad} after ${LOAD_CONFIG.maxRetries} attempts, skipping`,
			);
		}
	} finally {
		// Decrement active load counter
		state.activeLoads--;
		state.pagesInFlight.delete(pageToLoad);

		// Update loading state
		const stillLoading = state.loadingQueue.length > 0 || state.activeLoads > 0;
		state.isLoading = stillLoading;

		// Continue loading next page
		if (state.loadingQueue.length > 0) {
			setTimeout(() => loadNextPage(), 50);
		} else if (state.activeLoads === 0) {
			// All done - trigger final update
			state.allDataLoaded = true;
			updateUI();

			// Force a display update to refresh the result count
			displayProducts();

			// Save to cache
			if (CACHE_CONFIG.enabled) {
				console.log('💾 Attempting to save to cache...');
				saveToCache();
			}
		}
	}
}

function prioritizePageLoad(displayPage) {
	// displayPage is a grid page (itemsPerPage), which is not the same size as a
	// source page - translate it into the source pages that cover those products.
	const firstItem = (displayPage - 1) * state.itemsPerPage;
	const lastItem = firstItem + state.itemsPerPage - 1;
	const firstSourcePage = Math.floor(firstItem / state.sourcePageSize) + 1;
	const lastSourcePage = Math.min(
		Math.floor(lastItem / state.sourcePageSize) + 1,
		state.sourceTotalPages,
	);

	let queued = false;
	for (let page = lastSourcePage; page >= firstSourcePage; page--) {
		if (state.loadedPages.has(page) || state.pagesInFlight.has(page)) continue;

		// Remove page from queue if it exists, then re-add at the front
		const index = state.loadingQueue.indexOf(page);
		if (index > -1) {
			state.loadingQueue.splice(index, 1);
		}

		state.loadingQueue.unshift(page);
		queued = true;
	}

	// Trigger loading if not currently loading
	if (queued && !state.isLoading) {
		loadNextPage();
	}
}

// ============================================================================
// URL Parameter Management
// ============================================================================

function parseFiltersFromURL() {
	const params = new URLSearchParams(window.location.search);

	// Parse production type
	const production = params.get('production');
	if (production) {
		state.filters.production_type = production;

		// Update custom dropdown
		if (state.dom.filterProductionDropdown) {
			const option = state.dom.filterProductionDropdown.querySelector(
				`[data-value="${production}"]`,
			);
			if (option) {
				const label = option.querySelector('.option-label').textContent;
				const valueSpan = state.dom.filterProductionTrigger.querySelector(
					'.custom-select-value',
				);
				valueSpan.textContent = label;
				valueSpan.classList.remove('placeholder');

				// Mark option as selected
				state.dom.filterProductionDropdown
					.querySelectorAll('.custom-select-option')
					.forEach((opt) => opt.classList.remove('selected'));
				option.classList.add('selected');
			}
		}
	}

	// Parse clothing types (multi-select)
	const clothingTypes = params.get('clothing');
	if (clothingTypes) {
		const types = clothingTypes.split(',');
		types.forEach((type) => {
			state.filters.filter_types.add(type);
			// Activate the corresponding icon button
			const btn = document.querySelector(
				`.filter-icon-btn[data-filter-value="${type}"]`,
			);
			if (btn) {
				btn.classList.add('active');
			}
		});
	}

	// Parse MOQ
	const moq = params.get('moq');
	if (moq) {
		state.filters.moq = parseInt(moq);

		// Update custom dropdown
		if (state.dom.filterMoqDropdown) {
			const option = state.dom.filterMoqDropdown.querySelector(
				`[data-value="${moq}"]`,
			);
			if (option) {
				const label = option.querySelector('.option-label').textContent;
				const valueSpan = state.dom.filterMoqTrigger.querySelector(
					'.custom-select-value',
				);
				valueSpan.textContent = label;
				valueSpan.classList.remove('placeholder');

				// Mark option as selected
				state.dom.filterMoqDropdown
					.querySelectorAll('.custom-select-option')
					.forEach((opt) => opt.classList.remove('selected'));
				option.classList.add('selected');
			}
		}
	}

	// Parse price range
	const priceRange = params.get('price');
	if (priceRange) {
		// Update custom dropdown
		if (state.dom.filterPriceDropdown) {
			const option = state.dom.filterPriceDropdown.querySelector(
				`[data-value="${priceRange}"]`,
			);
			if (option) {
				const label = option.querySelector('.option-label').textContent;
				const valueSpan = state.dom.filterPriceTrigger.querySelector(
					'.custom-select-value',
				);
				valueSpan.textContent = label;
				valueSpan.classList.remove('placeholder');

				// Mark option as selected
				state.dom.filterPriceDropdown
					.querySelectorAll('.custom-select-option')
					.forEach((opt) => opt.classList.remove('selected'));
				option.classList.add('selected');
			}
		}

		const [min, max] = priceRange.split('-').map((v) => parseFloat(v));
		state.filters.price_min = min;
		state.filters.price_max = max || 999999;
	}

	// Parse name search
	const name = params.get('name');
	if (name) {
		state.filters.name = name;
		state.dom.filterName.value = name;
	}
}

function updateURLParams() {
	const params = new URLSearchParams();

	// Add active filters to params
	if (state.filters.production_type) {
		params.set('production', state.filters.production_type);
	}

	if (state.filters.filter_types.size > 0) {
		// Convert Set to comma-separated string
		params.set('clothing', Array.from(state.filters.filter_types).join(','));
	}

	if (state.filters.moq !== null) {
		params.set('moq', state.filters.moq.toString());
	}

	if (state.filters.price_min !== null && state.filters.price_max !== null) {
		// Reconstruct the price range string
		params.set(
			'price',
			`${state.filters.price_min}-${state.filters.price_max}`,
		);
	}

	if (state.filters.name) {
		params.set('name', state.filters.name);
	}

	// Update URL without reloading page
	const newURL =
		params.toString() === ''
			? window.location.pathname
			: `${window.location.pathname}?${params.toString()}`;

	window.history.replaceState({}, '', newURL);
}

// ============================================================================
// Filtering
// ============================================================================

function applyFilters() {
	applyFiltersWithoutPageReset();

	// Reset to page 1 when filters change
	state.currentPage = 1;

	// Display products
	displayProducts();
}

function applyFiltersWithoutPageReset() {
	// Check which filters are active
	const hasProductionType = !!state.filters.production_type;
	const hasFilterTypes = state.filters.filter_types.size > 0; // Check if any clothing types selected
	const hasMoq = state.filters.moq !== null;
	const hasPriceRange =
		state.filters.price_min !== null && state.filters.price_max !== null;
	const hasNameSearch = !!state.filters.name;
	const searchTerm = hasNameSearch ? state.filters.name.toLowerCase() : '';

	// Single-pass filtering for better performance
	const filtered = state.allProducts.filter((p) => {
		const meta = p.metafields;

		// Apply production type filter
		if (hasProductionType) {
			if (
				!meta.production_type ||
				meta.production_type !== state.filters.production_type
			) {
				return false;
			}
		}

		// Apply clothing type filter - product must match ANY of the selected types (OR logic)
		if (hasFilterTypes) {
			if (
				!meta.filter_type ||
				!state.filters.filter_types.has(meta.filter_type)
			) {
				return false;
			}
		}

		// Apply MOQ filter
		if (hasMoq) {
			if (
				!meta.minimum_order_qty ||
				meta.minimum_order_qty > state.filters.moq
			) {
				return false;
			}
		}

		// Apply price range filter
		if (hasPriceRange) {
			// Convert to numbers and handle missing/zero values
			const productPriceMin = parseFloat(meta.price_min) || 0;
			const productPriceMax = parseFloat(meta.price_max) || 0;

			// Check if product price range overlaps with filter range
			if (
				productPriceMin > state.filters.price_max ||
				productPriceMax < state.filters.price_min
			) {
				return false;
			}
		}

		// Apply name search - use pre-computed lowercase title
		if (hasNameSearch) {
			if (!p.titleLower.includes(searchTerm)) {
				return false;
			}
		}

		return true;
	});

	state.filteredProducts = filtered;

	// Update URL params to reflect current filters
	updateURLParams();

	// Update pagination
	updatePagination();
}

function displayProducts() {
	// Batch DOM updates using requestAnimationFrame for better performance
	requestAnimationFrame(() => {
		// Calculate which products to show
		const startIndex = (state.currentPage - 1) * state.itemsPerPage;
		const endIndex = startIndex + state.itemsPerPage;
		const productsToShow = state.filteredProducts.slice(startIndex, endIndex);

		// Create Set of product IDs that should be visible
		const shouldBeVisible = new Set(productsToShow.map((p) => p.id));

		// Hide ALL products that either:
		// 1. Are not in the filtered results, OR
		// 2. Are in filtered results but not on current page
		state.allProducts.forEach((p) => {
			if (!shouldBeVisible.has(p.id)) {
				p.element.className = 'product-card-wrapper hidden';
			}
		});

		// Show products that should be visible
		productsToShow.forEach((p) => {
			// Lazy render the product card HTML if not already rendered
			if (!p.rendered) {
				renderProductCard(p);
				p.rendered = true;
			}
			// Update className to visible
			p.element.className = 'product-card-wrapper visible';
		});

		// Update visible products tracking
		state.visibleProductIds = shouldBeVisible;

		// Show empty state if no products
		if (state.filteredProducts.length === 0) {
			showEmptyState();
		} else {
			hideEmptyState();
		}

		// Update result count
		updateResultCount();
	});
}

// Escape merchant-authored text before interpolating it into the card template
// below. Titles and alt text routinely contain apostrophes, which would
// otherwise close a single-quoted attribute and mangle the card markup.
function escapeHTML(value) {
	if (value === null || value === undefined) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function renderProductCard(product) {
	// Generate product card HTML from metadata
	const placeholder = product.element.querySelector(
		'.product-card-placeholder',
	);
	if (!placeholder) return;

	// Handle featured image - use placeholder if not available
	let imageHTML = '';
	if (product.featured_image) {
		imageHTML = `
			<div class='product-card__media'>
				<div class='product-card__image-wrapper'>
					<img
						src='${escapeHTML(product.featured_image)}'
						alt='${escapeHTML(product.featured_image_alt || product.title)}'
						class='product-card__image'
						loading='lazy'
					>`;

		// Add secondary image if available
		if (product.secondary_image) {
			imageHTML += `
					<img
						src='${escapeHTML(product.secondary_image)}'
						alt='${escapeHTML(product.featured_image_alt || product.title)}'
						class='product-card__image product-card__image--secondary'
						loading='lazy'
					>`;
		}

		imageHTML += `
				</div>
			</div>`;
	} else {
		imageHTML = `
			<div class='product-card__media'>
				<div class='product-card__no-image'>No Image</div>
			</div>`;
	}

	// Parse prices and handle fallback
	const priceMin = parseFloat(product.metafields.price_min) || 0;
	const priceMax = parseFloat(product.metafields.price_max) || 0;

	// Format price display
	let priceDisplay = '';
	if (priceMin > 0 && priceMax > 0) {
		if (priceMin === priceMax) {
			priceDisplay = `$${priceMin.toFixed(2)}`;
		} else {
			priceDisplay = `$${priceMin.toFixed(2)} - $${priceMax.toFixed(2)}`;
		}
	} else if (priceMin > 0) {
		priceDisplay = `$${priceMin.toFixed(2)}`;
	} else if (priceMax > 0) {
		priceDisplay = `$${priceMax.toFixed(2)}`;
	} else {
		priceDisplay = 'Price not available';
	}

	const cardHTML = `
		<div class='product-card'
			data-production-type='${escapeHTML(product.metafields.production_type)}'
			data-clothing-type='${escapeHTML(product.metafields.filter_type)}'
			data-min-order='${escapeHTML(product.metafields.minimum_order_qty)}'
			data-price-min='${priceMin}'
			data-price-max='${priceMax}'
			data-title='${escapeHTML(product.title.toLowerCase())}'>
			<a href='${escapeHTML(product.url)}' class='product-card__link'>
				${imageHTML}
				<div class='product-card__info'>
					<h5 class='product-card__production-type'>${escapeHTML(product.metafields.production_type_name)}</h5>
					<h4 class='product-card__title'>${escapeHTML(product.title)}</h4>
					<span class='product-card__price'>
						${priceDisplay}
					</span>
				</div>
			</a>
		</div>
	`;

	placeholder.innerHTML = cardHTML;
}

function showEmptyState() {
	let emptyState = document.getElementById('empty-state');
	if (!emptyState) {
		emptyState = document.createElement('div');
		emptyState.id = 'empty-state';
		state.dom.productGrid.appendChild(emptyState);
	}

	// Update message based on loading state
	if (!state.allDataLoaded) {
		emptyState.innerHTML = `<p>No products match your filters yet. Products are still loading... (${state.allProducts.length}/${state.totalProducts})</p>`;
	} else {
		emptyState.innerHTML =
			'<p>No products match your filters. Try adjusting your selections.</p>';
	}

	emptyState.style.display = 'block';
}

function hideEmptyState() {
	const emptyState = document.getElementById('empty-state');
	if (emptyState) {
		emptyState.style.display = 'none';
	}
}

// ============================================================================
// Pagination
// ============================================================================

function updatePagination() {
	state.totalPages = Math.ceil(
		state.filteredProducts.length / state.itemsPerPage,
	);

	// Ensure current page is valid
	if (state.currentPage > state.totalPages) {
		state.currentPage = Math.max(1, state.totalPages);
	}

	renderPaginationUI();
}

function renderPaginationUI() {
	if (!state.dom.pageNumbers) return;

	state.dom.pageNumbers.innerHTML = '';

	if (state.totalPages <= 1) {
		// Still show page 1 button, but disabled
		const button = document.createElement('button');
		button.textContent = '1';
		button.className = 'btn page-number active';
		button.disabled = true;
		state.dom.pageNumbers.appendChild(button);

		state.dom.btnPrevious.disabled = true;
		state.dom.btnNext.disabled = true;
		return;
	}

	// Generate page numbers to show
	const pagesToShow = generatePageNumbers();

	pagesToShow.forEach((page) => {
		if (page === '...') {
			const ellipsis = document.createElement('span');
			ellipsis.textContent = '...';
			ellipsis.className = 'pagination-ellipsis';
			state.dom.pageNumbers.appendChild(ellipsis);
		} else {
			const button = document.createElement('button');
			button.textContent = page;
			button.className = 'btn page-number';

			if (page === state.currentPage) {
				button.classList.add('active');
			}

			// Only disable if trying to go beyond loaded data AND not all data loaded
			const pageDataLoaded = isPageDataLoaded(page);
			if (!pageDataLoaded && !state.allDataLoaded) {
				button.classList.add('loading');
				button.title = 'Loading...';
			}

			button.addEventListener('click', () => goToPage(page));
			state.dom.pageNumbers.appendChild(button);
		}
	});

	// Update Previous/Next buttons
	state.dom.btnPrevious.disabled = state.currentPage === 1;
	state.dom.btnNext.disabled = state.currentPage === state.totalPages;
}

function generatePageNumbers() {
	const pages = [];
	const current = state.currentPage;
	const total = state.totalPages;

	// Always show first page
	pages.push(1);

	if (total <= 7) {
		// Show all pages
		for (let i = 2; i <= total; i++) {
			pages.push(i);
		}
	} else {
		// Smart pagination: [1] [2] [3] ... [15] ... [28]

		if (current <= 3) {
			// Near start: [1] [2] [3] [4] ... [28]
			pages.push(2, 3, 4);
			if (current < total - 1) pages.push('...');
		} else if (current >= total - 2) {
			// Near end: [1] ... [26] [27] [28]
			pages.push('...');
			pages.push(total - 2, total - 1);
		} else {
			// Middle: [1] ... [14] [15] [16] ... [28]
			pages.push('...');
			pages.push(current - 1, current, current + 1);
			pages.push('...');
		}

		// Always show last page
		if (!pages.includes(total)) {
			pages.push(total);
		}
	}

	return pages;
}

function isPageDataLoaded(page) {
	const startIndex = (page - 1) * state.itemsPerPage;
	const endIndex = startIndex + state.itemsPerPage;
	const productsNeeded = state.filteredProducts.slice(startIndex, endIndex);

	// All products for this page must be loaded
	return productsNeeded.every((p) => state.allProducts.includes(p));
}

function goToPage(page) {
	if (page < 1 || page > state.totalPages) return;

	// Check if page data is loaded
	if (!isPageDataLoaded(page) && !state.allDataLoaded) {
		// Prioritize loading this page
		prioritizePageLoad(page);

		// Wait and try again
		setTimeout(() => {
			if (isPageDataLoaded(page)) {
				goToPage(page);
			}
		}, 500);
		return;
	}

	state.currentPage = page;
	displayProducts();
	updatePagination();

	// Scroll to top
	document
		.getElementById('product-catalog')
		.scrollIntoView({ behavior: 'smooth' });
}

// ============================================================================
// Event Listeners
// ============================================================================

function setupCustomDropdown(wrapper, trigger, dropdown, onChange) {
	if (!wrapper || !trigger || !dropdown) return;

	// Toggle dropdown open/close
	trigger.addEventListener('click', (e) => {
		e.stopPropagation();

		// Close other custom dropdowns
		document.querySelectorAll('.custom-select.open').forEach((el) => {
			if (el !== wrapper) {
				el.classList.remove('open');
			}
		});

		wrapper.classList.toggle('open');
	});

	// Handle option selection
	const options = dropdown.querySelectorAll('.custom-select-option');
	options.forEach((option) => {
		option.addEventListener('click', (e) => {
			e.stopPropagation();

			const value = option.getAttribute('data-value');
			const label = option.querySelector('.option-label').textContent;

			// Update selected state
			options.forEach((opt) => opt.classList.remove('selected'));
			option.classList.add('selected');

			// Update trigger text
			const valueSpan = trigger.querySelector('.custom-select-value');
			valueSpan.textContent = label;
			valueSpan.classList.remove('placeholder');

			// Close dropdown
			wrapper.classList.remove('open');

			// Call onChange callback
			if (onChange) {
				onChange(value);
			}
		});
	});

	// Close dropdown when clicking outside
	document.addEventListener('click', (e) => {
		if (!wrapper.contains(e.target)) {
			wrapper.classList.remove('open');
		}
	});

	// Close on Escape key
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && wrapper.classList.contains('open')) {
			wrapper.classList.remove('open');
			trigger.focus();
		}
	});
}

function setupEventListeners() {
	// Custom dropdown - Production Method
	setupCustomDropdown(
		state.dom.filterProductionWrapper,
		state.dom.filterProductionTrigger,
		state.dom.filterProductionDropdown,
		(value) => {
			state.filters.production_type = value;
			applyFilters();
		},
	);

	// Custom dropdown - Minimum Order
	setupCustomDropdown(
		state.dom.filterMoqWrapper,
		state.dom.filterMoqTrigger,
		state.dom.filterMoqDropdown,
		(value) => {
			state.filters.moq = value ? parseInt(value) : null;
			applyFilters();
		},
	);

	// Custom dropdown - Price Range
	setupCustomDropdown(
		state.dom.filterPriceWrapper,
		state.dom.filterPriceTrigger,
		state.dom.filterPriceDropdown,
		(value) => {
			if (!value) {
				state.filters.price_min = null;
				state.filters.price_max = null;
			} else {
				const [min, max] = value.split('-').map((v) => parseFloat(v));
				state.filters.price_min = min;
				state.filters.price_max = max || 999999;
			}
			applyFilters();
		},
	);

	// Multi-select toggle clothing type filter
	const clothingIconButtons = document.querySelectorAll('.filter-icon-btn');
	clothingIconButtons.forEach((btn) => {
		btn.addEventListener('click', (e) => {
			const button = e.currentTarget;
			const filterValue = button.getAttribute('data-filter-value');

			// Toggle selection
			if (state.filters.filter_types.has(filterValue)) {
				// Remove from selection
				state.filters.filter_types.delete(filterValue);
				button.classList.remove('active');
			} else {
				// Add to selection
				state.filters.filter_types.add(filterValue);
				button.classList.add('active');
			}

			// Apply filter
			applyFilters();
		});
	});

	state.dom.filterName.addEventListener('input', (e) => {
		const value = e.target.value;

		// Toggle clear button visibility
		toggleClearButton(state.dom.filterName);

		// Clear existing debounce timer
		if (state.searchDebounceTimer) {
			clearTimeout(state.searchDebounceTimer);
		}

		// Debounce the search by 300ms to avoid filtering on every keystroke
		state.searchDebounceTimer = setTimeout(() => {
			state.filters.name = value;
			applyFilters();
		}, LOAD_CONFIG.searchDebounceMs);
	});

	state.dom.clearFilters.addEventListener('click', () => {
		clearFilters();
	});

	// Custom dropdown - Items Per Page
	setupCustomDropdown(
		state.dom.itemsPerPageWrapper,
		state.dom.itemsPerPageTrigger,
		state.dom.itemsPerPageDropdown,
		(value) => {
			state.itemsPerPage = parseInt(value);
			state.currentPage = 1;
			updateURLParams();
			updatePagination();
			displayProducts();
		},
	);

	// Pagination buttons
	state.dom.btnPrevious.addEventListener('click', () => {
		goToPage(state.currentPage - 1);
	});

	state.dom.btnNext.addEventListener('click', () => {
		goToPage(state.currentPage + 1);
	});

	// Mobile filter event listeners
	setupMobileFilterListeners();

	// Setup clear button functionality for filter name inputs
	setupClearButtonListeners();
}

// ============================================================================
// Mobile Filter Functionality
// ============================================================================

function setupClearButtonListeners() {
	// Desktop filter name
	const desktopInput = state.dom.filterName;
	const desktopClearBtn = desktopInput
		? desktopInput.parentElement.querySelector('.filter-name-clear')
		: null;

	if (desktopInput && desktopClearBtn) {
		// Initial state
		toggleClearButton(desktopInput);

		// Click handler for clear button
		desktopClearBtn.addEventListener('click', () => {
			desktopInput.value = '';
			state.filters.name = '';
			toggleClearButton(desktopInput);
			applyFilters();
			desktopInput.focus();
		});
	}

	// Mobile filter name
	const mobileInput = state.dom.mobileFilterName;
	const mobileClearBtn = mobileInput
		? mobileInput.parentElement.querySelector('.filter-name-clear')
		: null;

	if (mobileInput && mobileClearBtn) {
		// Initial state
		toggleClearButton(mobileInput);

		// Input handler to show/hide clear button
		mobileInput.addEventListener('input', () => {
			toggleClearButton(mobileInput);
		});

		// Click handler for clear button
		mobileClearBtn.addEventListener('click', () => {
			mobileInput.value = '';
			toggleClearButton(mobileInput);
			mobileInput.focus();
		});
	}
}

function toggleClearButton(input) {
	if (!input) return;

	const clearBtn = input.parentElement.querySelector('.filter-name-clear');
	if (!clearBtn) return;

	if (input.value.length > 0) {
		clearBtn.classList.add('visible');
	} else {
		clearBtn.classList.remove('visible');
	}
}

function setupMobileFilterListeners() {
	// Show/Hide drawer
	if (state.dom.showFiltersBtn) {
		state.dom.showFiltersBtn.addEventListener('click', openMobileFilters);
	}

	if (state.dom.closeFiltersBtn) {
		state.dom.closeFiltersBtn.addEventListener('click', closeMobileFilters);
	}

	if (state.dom.mobileFilterOverlay) {
		state.dom.mobileFilterOverlay.addEventListener('click', closeMobileFilters);
	}

	// Toggle button groups (Production, MOQ, Price)
	const toggleGroups = document.querySelectorAll('.toggle-button-group');
	toggleGroups.forEach((group) => {
		const filterType = group.getAttribute('data-filter');
		const buttons = group.querySelectorAll('.toggle-btn');

		buttons.forEach((btn) => {
			btn.addEventListener('click', () => {
				const isCurrentlyActive = btn.classList.contains('active');

				// Remove active from all buttons in this group
				buttons.forEach((b) => b.classList.remove('active'));

				// Toggle current button - if it was active, leave it off; if inactive, turn it on
				if (!isCurrentlyActive) {
					btn.classList.add('active');
				}
			});
		});
	});

	// Mobile icon buttons for filter type
	const mobileClothingButtons = document.querySelectorAll(
		'.mobile-filter-icon-btn',
	);
	mobileClothingButtons.forEach((btn) => {
		btn.addEventListener('click', () => {
			btn.classList.toggle('active');
		});
	});

	// Mobile name filter - just update the input, don't apply yet
	if (state.dom.mobileFilterName) {
		state.dom.mobileFilterName.addEventListener('input', (e) => {
			// Just update the value, apply on "Apply Filters" click
		});
	}

	// Apply filters button
	if (state.dom.mobileApplyBtn) {
		state.dom.mobileApplyBtn.addEventListener('click', applyMobileFilters);
	}

	// Reset filters button
	if (state.dom.mobileResetBtn) {
		state.dom.mobileResetBtn.addEventListener('click', resetMobileFilters);
	}
}

function openMobileFilters() {
	// Sync mobile filter UI with current filter state
	syncMobileFiltersFromState();

	// Validate drawer and overlay exist
	if (!state.dom.mobileFilterDrawer || !state.dom.mobileFilterOverlay) {
		console.warn('Mobile filter elements not found');
		return;
	}

	// Show drawer and overlay
	state.dom.mobileFilterDrawer.classList.add('active');
	state.dom.mobileFilterOverlay.classList.add('active');

	// Prevent body scroll
	document.body.style.overflow = 'hidden';
}

function closeMobileFilters() {
	// Validate elements exist
	if (!state.dom.mobileFilterDrawer || !state.dom.mobileFilterOverlay) {
		return;
	}

	// Hide drawer and overlay
	state.dom.mobileFilterDrawer.classList.remove('active');
	state.dom.mobileFilterOverlay.classList.remove('active');

	// Restore body scroll
	document.body.style.overflow = '';
}

function syncMobileFiltersFromState() {
	// Sync production method
	const productionButtons = document.querySelectorAll(
		'[data-filter="production"] .toggle-btn',
	);
	productionButtons.forEach((btn) => {
		if (btn.getAttribute('data-value') === state.filters.production_type) {
			btn.classList.add('active');
		} else {
			btn.classList.remove('active');
		}
	});

	// Sync filter types (clothing icons)
	const mobileClothingButtons = document.querySelectorAll(
		'.mobile-filter-icon-btn',
	);
	mobileClothingButtons.forEach((btn) => {
		const filterValue = btn.getAttribute('data-filter-value');
		if (state.filters.filter_types.has(filterValue)) {
			btn.classList.add('active');
		} else {
			btn.classList.remove('active');
		}
	});

	// Sync MOQ
	const moqButtons = document.querySelectorAll(
		'[data-filter="moq"] .toggle-btn',
	);
	moqButtons.forEach((btn) => {
		if (
			state.filters.moq &&
			btn.getAttribute('data-value') === state.filters.moq.toString()
		) {
			btn.classList.add('active');
		} else {
			btn.classList.remove('active');
		}
	});

	// Sync price range
	const priceButtons = document.querySelectorAll(
		'[data-filter="price"] .toggle-btn',
	);
	priceButtons.forEach((btn) => {
		const value = btn.getAttribute('data-value');
		if (state.filters.price_min !== null && state.filters.price_max !== null) {
			const currentRange = `${state.filters.price_min}-${state.filters.price_max}`;
			if (value === currentRange) {
				btn.classList.add('active');
			} else {
				btn.classList.remove('active');
			}
		} else {
			btn.classList.remove('active');
		}
	});

	// Sync name filter
	if (state.dom.mobileFilterName) {
		state.dom.mobileFilterName.value = state.filters.name || '';
		toggleClearButton(state.dom.mobileFilterName);
	}
}

function applyMobileFilters() {
	// Read production method
	const activeProductionBtn = document.querySelector(
		'[data-filter="production"] .toggle-btn.active',
	);
	state.filters.production_type = activeProductionBtn
		? activeProductionBtn.getAttribute('data-value')
		: '';

	// Read filter types (clothing icons)
	const activeClothingBtns = document.querySelectorAll(
		'.mobile-filter-icon-btn.active',
	);
	state.filters.filter_types = new Set();
	activeClothingBtns.forEach((btn) => {
		state.filters.filter_types.add(btn.getAttribute('data-filter-value'));
	});

	// Read MOQ
	const activeMoqBtn = document.querySelector(
		'[data-filter="moq"] .toggle-btn.active',
	);
	state.filters.moq = activeMoqBtn
		? parseInt(activeMoqBtn.getAttribute('data-value'))
		: null;

	// Read price range
	const activePriceBtn = document.querySelector(
		'[data-filter="price"] .toggle-btn.active',
	);
	if (activePriceBtn) {
		const value = activePriceBtn.getAttribute('data-value');
		const [min, max] = value.split('-').map((v) => parseFloat(v));
		state.filters.price_min = min;
		state.filters.price_max = max || 999999;
	} else {
		state.filters.price_min = null;
		state.filters.price_max = null;
	}

	// Read name filter
	state.filters.name = state.dom.mobileFilterName
		? state.dom.mobileFilterName.value
		: '';

	// Apply filters and close drawer
	applyFilters();
	closeMobileFilters();

	// Sync desktop filters to match mobile
	syncDesktopFiltersFromState();
}

function resetMobileFilters() {
	// Clear all toggle buttons
	document
		.querySelectorAll('.toggle-btn')
		.forEach((btn) => btn.classList.remove('active'));
	document
		.querySelectorAll('.mobile-filter-icon-btn')
		.forEach((btn) => btn.classList.remove('active'));

	// Clear name input
	if (state.dom.mobileFilterName) {
		state.dom.mobileFilterName.value = '';
		toggleClearButton(state.dom.mobileFilterName);
	}
}

function syncDesktopFiltersFromState() {
	// Sync desktop filter controls with state

	// Production type
	if (state.dom.filterProductionTrigger && state.dom.filterProductionDropdown) {
		const value = state.filters.production_type || '';
		const option = state.dom.filterProductionDropdown.querySelector(
			`[data-value="${value}"]`,
		);
		if (option) {
			const label = option.querySelector('.option-label').textContent;
			const valueSpan = state.dom.filterProductionTrigger.querySelector(
				'.custom-select-value',
			);
			valueSpan.textContent = label;
			if (value) {
				valueSpan.classList.remove('placeholder');
			} else {
				valueSpan.classList.add('placeholder');
			}
			state.dom.filterProductionDropdown
				.querySelectorAll('.custom-select-option')
				.forEach((opt) => opt.classList.remove('selected'));
			option.classList.add('selected');
		}
	}

	// Sync desktop icon buttons
	const desktopClothingButtons = document.querySelectorAll('.filter-icon-btn');
	desktopClothingButtons.forEach((btn) => {
		const filterValue = btn.getAttribute('data-filter-value');
		if (state.filters.filter_types.has(filterValue)) {
			btn.classList.add('active');
		} else {
			btn.classList.remove('active');
		}
	});

	// MOQ
	if (state.dom.filterMoqTrigger && state.dom.filterMoqDropdown) {
		const value = state.filters.moq ? state.filters.moq.toString() : '';
		const option = state.dom.filterMoqDropdown.querySelector(
			`[data-value="${value}"]`,
		);
		if (option) {
			const label = option.querySelector('.option-label').textContent;
			const valueSpan = state.dom.filterMoqTrigger.querySelector(
				'.custom-select-value',
			);
			valueSpan.textContent = label;
			if (value) {
				valueSpan.classList.remove('placeholder');
			} else {
				valueSpan.classList.add('placeholder');
			}
			state.dom.filterMoqDropdown
				.querySelectorAll('.custom-select-option')
				.forEach((opt) => opt.classList.remove('selected'));
			option.classList.add('selected');
		}
	}

	// Price
	if (state.dom.filterPriceTrigger && state.dom.filterPriceDropdown) {
		let value = '';
		if (state.filters.price_min !== null && state.filters.price_max !== null) {
			value = `${state.filters.price_min}-${state.filters.price_max}`;
		}
		const option = state.dom.filterPriceDropdown.querySelector(
			`[data-value="${value}"]`,
		);
		if (option) {
			const label = option.querySelector('.option-label').textContent;
			const valueSpan = state.dom.filterPriceTrigger.querySelector(
				'.custom-select-value',
			);
			valueSpan.textContent = label;
			if (value) {
				valueSpan.classList.remove('placeholder');
			} else {
				valueSpan.classList.add('placeholder');
			}
			state.dom.filterPriceDropdown
				.querySelectorAll('.custom-select-option')
				.forEach((opt) => opt.classList.remove('selected'));
			option.classList.add('selected');
		}
	}

	if (state.dom.filterName) {
		state.dom.filterName.value = state.filters.name || '';
		toggleClearButton(state.dom.filterName);
	}
}

function clearFilters() {
	state.filters = {
		production_type: '',
		filter_types: new Set(),
		moq: null,
		price_min: null,
		price_max: null,
		name: '',
	};

	// Reset desktop UI
	if (state.dom.filterProductionTrigger && state.dom.filterProductionDropdown) {
		const valueSpan = state.dom.filterProductionTrigger.querySelector(
			'.custom-select-value',
		);
		valueSpan.textContent = 'Choose';
		valueSpan.classList.add('placeholder');
		state.dom.filterProductionDropdown
			.querySelectorAll('.custom-select-option')
			.forEach((opt) => opt.classList.remove('selected'));
		// Select the first option (All Production Types)
		const firstOption =
			state.dom.filterProductionDropdown.querySelector('[data-value=""]');
		if (firstOption) firstOption.classList.add('selected');
	}

	if (state.dom.filterMoqTrigger && state.dom.filterMoqDropdown) {
		const valueSpan = state.dom.filterMoqTrigger.querySelector(
			'.custom-select-value',
		);
		valueSpan.textContent = 'Any Quantity';
		valueSpan.classList.add('placeholder');
		state.dom.filterMoqDropdown
			.querySelectorAll('.custom-select-option')
			.forEach((opt) => opt.classList.remove('selected'));
		// Select the first option (Any Quantity)
		const firstOption =
			state.dom.filterMoqDropdown.querySelector('[data-value=""]');
		if (firstOption) firstOption.classList.add('selected');
	}

	if (state.dom.filterPriceTrigger && state.dom.filterPriceDropdown) {
		const valueSpan = state.dom.filterPriceTrigger.querySelector(
			'.custom-select-value',
		);
		valueSpan.textContent = 'Any Price';
		valueSpan.classList.add('placeholder');
		state.dom.filterPriceDropdown
			.querySelectorAll('.custom-select-option')
			.forEach((opt) => opt.classList.remove('selected'));
		// Select the first option (Any Price)
		const firstOption =
			state.dom.filterPriceDropdown.querySelector('[data-value=""]');
		if (firstOption) firstOption.classList.add('selected');
	}

	state.dom.filterName.value = '';
	toggleClearButton(state.dom.filterName);

	// Reset icon buttons - remove all active states
	const clothingIconButtons = document.querySelectorAll('.filter-icon-btn');
	clothingIconButtons.forEach((btn) => btn.classList.remove('active'));

	// Reset mobile UI
	resetMobileFilters();

	state.currentPage = 1;

	// Clear URL params
	window.history.replaceState({}, '', window.location.pathname);

	applyFilters();
}

// ============================================================================
// UI Updates
// ============================================================================

function updateUI() {
	updatePagination();
	updateResultCount();
}

function updateResultCount() {
	const startIndex = (state.currentPage - 1) * state.itemsPerPage + 1;
	const endIndex = Math.min(
		startIndex + state.itemsPerPage - 1,
		state.filteredProducts.length,
	);

	if (state.filteredProducts.length === 0) {
		if (!state.allDataLoaded) {
			state.dom.resultText.innerHTML = `No products found. Products are still loading... (${state.allProducts.length}/${state.totalProducts})`;
		} else {
			state.dom.resultText.textContent = 'No products found';
		}
	} else {
		// Show loading state if not all data is loaded
		if (!state.allDataLoaded) {
			state.dom.resultText.innerHTML = `Showing ${startIndex}-${endIndex} of ${state.filteredProducts.length} products <span style="color: #888; font-size: 0.9em;">(Loading all products... ${state.allProducts.length}/${state.totalProducts})</span>`;
		} else {
			state.dom.resultText.textContent = `Showing ${startIndex}-${endIndex} of ${state.filteredProducts.length} products`;
		}
	}
}

// ============================================================================
// Cache Management
// ============================================================================

function saveToCache() {
	try {
		const cacheData = {
			timestamp: Date.now(),
			collectionHandle: state.collectionHandle,
			products: state.allProducts.map((p) => ({
				id: p.id,
				title: p.title,
				titleLower: p.titleLower,
				url: p.url,
				featured_image: p.featured_image,
				featured_image_alt: p.featured_image_alt,
				secondary_image: p.secondary_image,
				metafields: p.metafields,
			})),
			totalPages: state.sourceTotalPages,
			totalProducts: state.totalProducts,
		};

		sessionStorage.setItem(CACHE_CONFIG.storageKey, JSON.stringify(cacheData));
		console.log('✅ Cache saved:', cacheData.products.length, 'products');
	} catch (error) {
		console.error('Error saving to cache:', error);
	}
}

function loadFromCache() {
	try {
		const cached = sessionStorage.getItem(CACHE_CONFIG.storageKey);
		if (!cached) {
			console.log('❌ No cache found in sessionStorage');
			return false;
		}

		const cacheData = JSON.parse(cached);
		console.log('📦 Cache found:', cacheData.products.length, 'products');

		// Check if cache is expired
		const age = Date.now() - cacheData.timestamp;
		const ageMinutes = Math.floor(age / 60000);
		console.log(
			'⏰ Cache age:',
			ageMinutes,
			'minutes (max:',
			CACHE_CONFIG.duration / 60000,
			'minutes)',
		);

		if (age > CACHE_CONFIG.duration) {
			sessionStorage.removeItem(CACHE_CONFIG.storageKey);
			console.log('❌ Cache expired, cleared');
			return false;
		}

		// Check if collection matches
		console.log(
			'🔍 Checking collection:',
			cacheData.collectionHandle,
			'vs',
			state.collectionHandle,
		);
		if (cacheData.collectionHandle !== state.collectionHandle) {
			console.log('❌ Cache collection mismatch');
			return false;
		}

		// Check if we have enough cached products to make it worthwhile
		console.log(
			'📊 Products in state:',
			state.allProducts.length,
			'vs cache:',
			cacheData.products.length,
		);
		if (cacheData.products.length <= state.allProducts.length) {
			console.log('❌ Cache has no additional products');
			return false;
		}

		console.log(
			'✅ Loading from cache:',
			cacheData.products.length,
			'products',
		);

		// Restore cached products by creating DOM elements for new products
		// Use DocumentFragment for batch DOM operations
		const fragment = document.createDocumentFragment();
		let addedCount = 0;

		cacheData.products.forEach((cachedProduct) => {
			// Skip if product already exists
			if (state.productIds.has(cachedProduct.id)) {
				return;
			}

			// Create a new wrapper element
			const wrapper = document.createElement('div');
			wrapper.className = 'product-card-wrapper hidden';

			// Add placeholder
			const placeholder = document.createElement('div');
			placeholder.className = 'product-card-placeholder';
			wrapper.appendChild(placeholder);

			// Add to fragment instead of DOM directly
			fragment.appendChild(wrapper);

			// Add to state
			state.allProducts.push({
				id: cachedProduct.id,
				title: cachedProduct.title,
				titleLower: cachedProduct.titleLower,
				url: cachedProduct.url,
				featured_image: cachedProduct.featured_image,
				featured_image_alt: cachedProduct.featured_image_alt,
				secondary_image: cachedProduct.secondary_image,
				metafields: cachedProduct.metafields,
				element: wrapper,
				rendered: false,
			});

			state.productIds.add(cachedProduct.id);
			addedCount++;
		});

		// Single DOM operation to add all elements at once
		if (addedCount > 0) {
			state.dom.productGrid.appendChild(fragment);
		}

		// Update state
		state.sourceTotalPages = cacheData.totalPages;
		state.totalProducts = cacheData.totalProducts;
		state.allDataLoaded = true;

		// Mark all pages as loaded
		for (let i = 1; i <= cacheData.totalPages; i++) {
			state.loadedPages.add(i);
		}

		console.log(
			'✅ Cache restored successfully, added',
			addedCount,
			'new products',
		);
		return true;
	} catch (error) {
		console.error('❌ Error loading from cache:', error);
		return false;
	}
}
